import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { SKILLS_VISIBLE_COUNT, formatDate, getDaysFromToday } from "./candidateHelpers";

const parseSkills = (skills) => {
    if (!skills) return [];
    if (typeof skills === "string") return skills.split(",").map((s) => s.trim()).filter(Boolean);
    return skills;
};

const SkillsCell = ({ skills = null, secondarySkills = null }) => {
    const primaryList = parseSkills(skills);
    const secondaryList = parseSkills(secondarySkills);
    const [popupPos, setPopupPos] = useState(null);
    const triggerRef = useRef(null);

    if (!primaryList.length && !secondaryList.length) return <span style={{ color: "#C3C3C3" }}>N/A</span>;

    const visible = primaryList.slice(0, SKILLS_VISIBLE_COUNT);
    const remainingPrimary = primaryList.slice(SKILLS_VISIBLE_COUNT);
    const extra = [...primaryList.slice(SKILLS_VISIBLE_COUNT), ...secondaryList];

    const showPopup = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPopupPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    };

    const hidePopup = () => setPopupPos(null);

    return (
        <div className="d-flex flex-wrap" style={{ gap: "5px" }}>
            {visible.map((skill, idx) => (
                <p className="candidate-skills" key={idx} style={{ background: '#d6e7ff', color: '#0048b7', padding: '2px 5px', borderRadius: '6px' }}>
                    {skill}
                </p>
            ))}
            {extra.length > 0 && (
                <>
                    <p
                        ref={triggerRef}
                        className="candidate-skills more-skills"
                        style={{ background: "#d6e7ff", color: "#0048b7", padding: "2px 5px", borderRadius: "6px", cursor: "pointer", margin: 0 }}
                        onMouseEnter={showPopup}
                        onMouseLeave={hidePopup}
                    >
                        +{extra.length} more
                    </p>
                    {popupPos && ReactDOM.createPortal(
                        <div
                            className="skills-popup"
                            style={{
                                position: "absolute",
                                top: popupPos.top,
                                left: popupPos.left,
                                zIndex: 9999,
                                pointerEvents: "none",
                                background: "#fff",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "4px",
                                height: "fit-content",
                                maxHeight: "200px",
                                overflowY: "auto",
                            }}
                        >
                            {extra.map((s, i) => (
                                <span className="popup-skill-tag" key={i}>{s}{i < extra.length - 1 && ", "}</span>
                            ))}
                        </div>,
                        document.body
                    )}
                </>
            )}
        </div>
    );
};

const LIST_VISIBLE_COUNT = 2;

const ListsCell = ({ lists = null }) => {
    const listArray = Array.isArray(lists) ? lists : (lists ? [lists] : []);
    const [popupPos, setPopupPos] = useState(null);
    const triggerRef = useRef(null);

    if (!listArray.length) return <span style={{ color: "#C3C3C3" }}>N/A</span>;

    const visible = listArray.slice(0, LIST_VISIBLE_COUNT);
    const extra = listArray.slice(LIST_VISIBLE_COUNT);

    const showPopup = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPopupPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    };

    const hidePopup = () => setPopupPos(null);

    return (
        <div className="d-flex flex-wrap" style={{ gap: "5px" }}>
            {visible.map((item, idx) => (
                <p className="candidate-skills" key={item.id || idx} style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 5px', borderRadius: '6px', margin: 0 }}>
                    {item.name}
                </p>
            ))}
            {extra.length > 0 && (
                <>
                    <p
                        ref={triggerRef}
                        className="candidate-skills more-skills"
                        style={{ background: "#e8f5e9", color: "#2e7d32", padding: "2px 5px", borderRadius: "6px", cursor: "pointer", margin: 0 }}
                        onMouseEnter={showPopup}
                        onMouseLeave={hidePopup}
                    >
                        +{extra.length} more
                    </p>
                    {popupPos && ReactDOM.createPortal(
                        <div
                            className="skills-popup"
                            style={{
                                position: "absolute",
                                top: popupPos.top,
                                left: popupPos.left,
                                zIndex: 9999,
                                pointerEvents: "none",
                                background: "#fff",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "4px",
                                height: "fit-content",
                                maxHeight: "200px",
                                overflowY: "auto",
                            }}
                        >
                            {extra.map((item, i) => (
                                <span className="popup-skill-tag" key={item.id || i}>{item.name}{i < extra.length - 1 && ", "}</span>
                            ))}
                        </div>,
                        document.body
                    )}
                </>
            )}
        </div>
    );
};

const CandidateTableBody = ({ candidates = [], onViewSummary, onDownload, onAddClick }) => {
    if (candidates.length === 0) {
        return (
            <tr>
                <td colSpan={6} style={{ textAlign: "center", color: "#C3C3C3", fontSize: "14px" }}>
                    No candidates yet. Click{" "}
                    <strong style={{ cursor: "pointer", color: "#F75891" }} onClick={onAddClick}>
                        Add Candidates
                    </strong>{" "}
                    to get started.
                </td>
            </tr>
        );
    }

    return candidates.map((c) => (
        <tr key={c.id}>
            <td style={{ paddingLeft: '30px' }}>
                <div className="d-flex flex-column gap-1">
                    <strong style={{ cursor: "pointer" }}>{c.full_name}</strong>
                    <p>{c.phone_number}</p>
                </div>
            </td>
            <td className="text-center">{c.email}</td>
            <td className="text-center">{c.current_designation || "N/A"}</td>
            <td className="text-center">
                <div className="d-flex flex-column gap-2">
                    <strong>{getDaysFromToday(c.created_at)}</strong>
                    <p>{formatDate(c.created_at)}</p>
                </div>
            </td>
            <td><SkillsCell skills={c.primary_skills} secondarySkills={c.secondary_skills} /></td>
            <td className="text-center">
                <ListsCell lists={c.lists} />
            </td>
            <td className="text-center">
                <div className="d-flex justify-content-center align-items-center gap-2">
                    <button
                        title="View Resume Summary"
                        style={{ background: "none", border: "none", cursor: "pointer", padding: "0" }}
                        onClick={() => onViewSummary(c)}
                    >
                        <img src="/images/eye-black-icon.svg" alt="View" width={20} height={20} />
                    </button>
                    <button
                        title="Download Resume"
                        style={{
                            background: "none",
                            border: "none",
                            cursor: c.resume_url ? "pointer" : "not-allowed",
                            padding: "0",
                            opacity: c.resume_url ? 1 : 0.4,
                        }}
                        disabled={!c.resume_url}
                        onClick={() => onDownload(c)}
                    >
                        <img src="/images/download.svg" alt="Download" width={18} height={18} />
                    </button>
                </div>
            </td>
        </tr>
    ));
};

export default CandidateTableBody;
